/**
 * Sesi: JWT dari POST /dashboard/login (ADR-043 — bukan API key, bukan database).
 *
 * Balasan login cuma { access_token, role, username } — TIDAK ada tenant_id dan
 * TIDAK ada masa berlaku. Jadi klien tidak punya cara tahu kapan tokennya habis
 * selain ditolak 401 oleh sebuah permintaan. Karena itu penanganan 401 di
 * `client.js` bukan pelengkap: ia satu-satunya sinyal, dan ia memanggil kembali
 * ke sini lewat `pasangPenangananKedaluwarsa`.
 *
 * TIGA KEADAAN, bukan dua:
 *
 *   masuk          `sesi` terisi
 *   sesi habis     `sesi` kosong, `sesiTerakhir` menyebut siapa yang tadi masuk
 *   keluar         keduanya kosong; `keluarSengaja` membedakannya dari belum pernah masuk
 *
 * Tampilan lama hanya membaca `sesi`: begitu kosong ia menampilkan layar masuk, persis
 * seperti sebelumnya. Tampilan rancang ulang membaca keduanya: selama `sesiTerakhir`
 * ada, halamannya TETAP terpasang di bawah dialog masuk lagi, jadi ketikan yang belum
 * disimpan tidak hilang (ADR-094 no. 8).
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useReducer, useRef } from 'react'
import {
  api, sesi as simpanan, pasangPenangananDitolak, pasangPenangananKedaluwarsa,
} from '../shared/api/client'
import { klienKueri } from './kueri'

const Konteks = createContext(null)

const SELANG_SEGAR = 5000

function keadaanAwal() {
  // KEDUANYA harus ada. Token tanpa sesi bukan "sesi yang perlu dipulihkan sebagian" —
  // ia keadaan yang tidak bisa dipercaya: perannya tidak diketahui, dan peran yang
  // tidak diketahui membuat `App.jsx` memilih dashboard yang salah sambil tetap
  // mengirim tokennya.
  //
  // Dibersihkan, bukan dibiarkan: token yang tertinggal akan menghidupkan keadaan yang
  // sama lagi pada pemuatan berikutnya.
  let sesi = simpanan.token() ? simpanan.baca() : null
  if (simpanan.token() && !sesi?.peran) {
    simpanan.hapus()
    sesi = null
  }
  return { sesi, sesiTerakhir: null, kedaluwarsa: false, keluarSengaja: false }
}

function ubah(k, aksi) {
  switch (aksi.jenis) {
    case 'masuk':
      return { sesi: aksi.sesi, sesiTerakhir: null, kedaluwarsa: false, keluarSengaja: false }
    case 'keluar':
      return { sesi: null, sesiTerakhir: null, kedaluwarsa: false, keluarSengaja: true }
    case 'peran':
      // Perannya berubah di database selagi orangnya masuk (ADR-095 no. 4).
      if (!k.sesi || (k.sesi.peran === aksi.peran && k.sesi.nama === aksi.nama)) return k
      return { ...k, sesi: { peran: aksi.peran, nama: aksi.nama } }
    case 'habis':
      // Beberapa permintaan bisa ditolak 401 sekaligus; yang kedua tidak boleh
      // menghapus siapa yang tadi masuk.
      return { ...k, sesi: null, sesiTerakhir: k.sesi ?? k.sesiTerakhir, kedaluwarsa: true }
    default:
      return k
  }
}

export function PenyediaSesi({ children }) {
  const [k, kirim] = useReducer(ubah, null, keadaanAwal)

  useEffect(() => {
    pasangPenangananKedaluwarsa(() => kirim({ jenis: 'habis' }))
    return () => pasangPenangananKedaluwarsa(null)
  }, [])

  // JAWABAN 403: tanyakan ke backend siapa orang ini SEKARANG. Kalau perannya sudah
  // berubah, sesinya diganti; menu dan penjaga rute mengikuti, dan data yang diambil
  // dengan peran lama diminta ulang. Kalau perannya sama, 403 itu memang penolakan biasa
  // dan tidak ada yang berubah.
  //
  // Paling sering sekali per SELANG_SEGAR: halaman yang ditolak di lima permintaan
  // sekaligus tidak perlu bertanya lima kali.
  //
  // Yang dibandingkan sesi DI MEMORI tab ini, bukan simpanan peramban. Simpanan itu milik
  // bersama: tab lain yang kena 403 lebih dulu sudah menulis peran barunya ke sana, dan
  // tab yang membandingkan dengan simpanan menyimpulkan "tidak ada yang berubah" sambil
  // tetap menampilkan menu peran lamanya.
  const segar = useRef({ sedang: false, terakhir: 0 })
  const sesiKini = useRef(k.sesi)
  useEffect(() => { sesiKini.current = k.sesi }, [k.sesi])
  useEffect(() => {
    pasangPenangananDitolak(async () => {
      const s = segar.current
      const token = simpanan.token()
      if (s.sedang || Date.now() - s.terakhir < SELANG_SEGAR || !token) return
      s.sedang = true
      try {
        const saya = await api.saya()
        // Jawabannya milik TOKEN yang bertanya. Kalau selagi ditunggu orangnya keluar dan
        // orang lain masuk, peran dan nama orang pertama tidak boleh ditulis di samping
        // token orang kedua.
        if (simpanan.token() !== token) return
        // Jawaban yang bentuknya tidak dikenal (bukan JSON, atau tanpa peran) dibuang:
        // menulisnya berarti sesi tanpa peran, dan orangnya terlempar dari halamannya.
        if (typeof saya?.role !== 'string' || typeof saya?.username !== 'string') return
        const lama = sesiKini.current
        if (lama && (lama.peran !== saya.role || lama.nama !== saya.username)) {
          simpanan.simpan(token, { peran: saya.role, nama: saya.username })
          kirim({ jenis: 'peran', peran: saya.role, nama: saya.username })
          klienKueri.invalidateQueries()
        }
      } catch {
        // 401 sudah ditangani jalur sesi habis. Galat lain: sesinya dibiarkan apa adanya.
      } finally {
        s.sedang = false
        s.terakhir = Date.now()
      }
    })
    return () => pasangPenangananDitolak(null)
  }, [])

  // Siapa yang terakhir memegang data di cache: yang sedang masuk, atau yang sesinya
  // baru saja habis.
  const pemilikCache = (k.sesi ?? k.sesiTerakhir)?.nama ?? null

  const masuk = useCallback(async (username, password) => {
    const data = await api.login(username, password)
    // Kunci kueri TanStack tidak memuat pengguna atau tenant. Orang yang SAMA masuk lagi
    // (sesi habis): datanya diminta ulang, dan yang lama tetap tampil selagi menunggu.
    // Orang LAIN, atau sesudah keluar: cache-nya dibuang, supaya data orang sebelumnya
    // tidak sempat tampil di perangkat bersama.
    if (pemilikCache && pemilikCache === data.username) klienKueri.invalidateQueries()
    else klienKueri.clear()
    kirim({ jenis: 'masuk', sesi: { peran: data.role, nama: data.username } })
    return data
  }, [pemilikCache])

  const keluar = useCallback(() => {
    api.keluar()
    kirim({ jenis: 'keluar' })
    klienKueri.clear()
  }, [])

  const nilai = useMemo(
    () => ({
      sesi: k.sesi,
      sesiTerakhir: k.sesiTerakhir,
      masuk,
      keluar,
      kedaluwarsa: k.kedaluwarsa,
      // Orangnya menekan Keluar. Layar masuk sesudahnya tidak boleh mengingat halaman
      // yang tadi dibuka: orang berikutnya di perangkat itu akan dibawa ke sana.
      keluarSengaja: k.keluarSengaja,
      // Penyaringan menu di frontend adalah kenyamanan, BUKAN keamanan: yang menjaga
      // izin adalah backend, per rute (ADR-095).
      punyaPeran: (...peran) => !!k.sesi && peran.includes(k.sesi.peran),
    }),
    [k, masuk, keluar],
  )

  return <Konteks.Provider value={nilai}>{children}</Konteks.Provider>
}

export function useSesi() {
  const k = useContext(Konteks)
  if (!k) throw new Error('useSesi dipakai di luar <PenyediaSesi>')
  return k
}
